import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { SliderField } from './atoms/SliderField'
import { calcularMetricasComerciais } from '../lib/metricasComerciaisCalculations'
import { useRaioXState } from '../hooks/useRaioXState'
import { metricasComerciaisDefaultValues } from '../constants'
import { formatCurrency, formatPercent } from '../lib/formatters'
import type { CalculatorResult } from '../types'

export function MetricasComerciaisPage() {
  const { values, updateValue } = useRaioXState(metricasComerciaisDefaultValues)

  const results = useMemo(() => calcularMetricasComerciais(values), [values])

  return (
    <div className="flex flex-col gap-6 max-w-[900px] mx-auto">
      <div>
        <h2 className="text-xl font-semibold tracking-[-0.022em] text-foreground">
          Métricas Comerciais
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Simulação completa do funil do investimento em tráfego até as vendas
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Inputs */}
        <Card className="bg-card border border-border rounded-2xl">
          <CardHeader className="pb-4 pt-5 px-5">
            <CardTitle className="text-sm font-semibold tracking-[-0.022em]">Parâmetros</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-5">
            <SliderField
              label="Investimento"
              value={values.investimento}
              onChange={(v) => updateValue('investimento', v)}
              min={500}
              max={100000}
              step={500}
              formatValue={formatCurrency}
            />
            <SliderField
              label="CPM"
              value={values.cpm}
              onChange={(v) => updateValue('cpm', v)}
              min={1}
              max={200}
              step={1}
              formatValue={formatCurrency}
            />
            <SliderField
              label="CTR"
              value={values.ctr}
              onChange={(v) => updateValue('ctr', v)}
              min={0.1}
              max={10}
              step={0.1}
              formatValue={(v) => formatPercent(v, 1)}
            />
            <SliderField
              label="Clique → Lead"
              value={values.taxaCliqueLead}
              onChange={(v) => updateValue('taxaCliqueLead', v)}
              min={1}
              max={100}
              step={1}
              unit="%"
            />
            <SliderField
              label="Lead → Agendamento"
              value={values.taxaLeadAgendamento}
              onChange={(v) => updateValue('taxaLeadAgendamento', v)}
              min={1}
              max={100}
              step={1}
              unit="%"
            />
            <SliderField
              label="Agendada → Realizada"
              value={values.taxaAgendadaRealizada}
              onChange={(v) => updateValue('taxaAgendadaRealizada', v)}
              min={1}
              max={100}
              step={1}
              unit="%"
            />
            <SliderField
              label="Consulta → Venda"
              value={values.taxaConsultaVenda}
              onChange={(v) => updateValue('taxaConsultaVenda', v)}
              min={1}
              max={100}
              step={1}
              unit="%"
            />
            <SliderField
              label="Ticket Médio"
              value={values.ticketMedio}
              onChange={(v) => updateValue('ticketMedio', v)}
              min={100}
              max={15000}
              step={50}
              formatValue={formatCurrency}
            />
          </CardContent>
        </Card>

        {/* Results */}
        <ResultsCard results={results} />
      </div>
    </div>
  )
}

function ResultsCard({ results }: { results: CalculatorResult[] }) {
  const traffic = results.slice(0, 3)
  const funnel = results.slice(3, 8)
  const economic = results.slice(8)

  return (
    <div className="space-y-4">
      <ResultGroup title="Tráfego" items={traffic} />
      <ResultGroup title="Funil Comercial" items={funnel} />
      <ResultGroup title="Resultados Econômicos" items={economic} />
    </div>
  )
}

function ResultGroup({ title, items }: { title: string; items: CalculatorResult[] }) {
  return (
    <Card className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border/60">
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {title}
        </span>
      </div>
      <div className="px-4 py-1">
        {items.map((r, i) => (
          <div key={i} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
            <span className="text-xs text-muted-foreground">{r.label}</span>
            <span
              className={cn(
                'text-sm font-semibold tabular-nums font-mono',
                r.status === 'success' && 'text-success',
                r.status === 'warning' && 'text-warning',
                r.status === 'danger' && 'text-destructive',
                (!r.status || r.status === 'accent') && 'text-foreground',
              )}
            >
              {r.formattedValue}
            </span>
          </div>
        ))}
      </div>
    </Card>
  )
}
